/**
 * Сохранение/восстановление сессии в localStorage.
 *
 * Соответствует UX-спеке v1.2 раздел 3:
 *  - 3.1: каждое значимое действие сохраняется в localStorage.
 *  - 3.2: при открытии Mini App пытаемся восстановить сессию.
 *  - 3.3: незавершённая сессия не старше 7 дней → модалка M4.
 *
 * Архитектура:
 *  - DI через параметр `storage` (для тестирования без DOM).
 *  - Версия схемы (CURRENT_VERSION) защищает от breaking-changes:
 *    при апгрейде схемы старые записи игнорируются и стираются.
 *  - TTL 7 дней — после стирается. Совпадает с UX-спекой.
 *
 * TODO Фаза 4: добавить session_id и POST на бэкенд после каждого сохранения
 * (UX-спека 3.1 — серверная синхронизация ответов).
 */

import type { SessionState } from '@/types/test';

export const STORAGE_KEY = 'aitiprofile.session';
export const CURRENT_VERSION = 1;
export const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface StorageEntry {
  version: number;
  /** Когда запись была создана/обновлена (Date.now()). */
  timestamp: number;
  data: SessionState;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Возвращает globalThis.localStorage или null, если он недоступен
 * (SSR, приватный режим браузера, тесты в node-окружении).
 */
function getDefaultStorage(): StorageLike | null {
  try {
    return (globalThis as { localStorage?: StorageLike }).localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * Сохраняет состояние сессии. На каждое значимое изменение state.
 * Тихо игнорирует ошибки (квота превышена, storage недоступен).
 */
export function saveSession(
  state: SessionState,
  storage: StorageLike | null = getDefaultStorage(),
): void {
  if (!storage) return;
  const entry: StorageEntry = {
    version: CURRENT_VERSION,
    timestamp: Date.now(),
    data: state,
  };
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Quota exceeded или storage заблокирован — игнорируем,
    // не ломаем UX. UI будет работать без persistence.
  }
}

/**
 * Загружает сессию. Возвращает null, если:
 *  - storage недоступен;
 *  - ничего не сохранено;
 *  - версия не совпадает с CURRENT_VERSION (стираем);
 *  - TTL истёк (стираем);
 *  - JSON повреждён (стираем).
 */
export function loadSession(
  storage: StorageLike | null = getDefaultStorage(),
): SessionState | null {
  if (!storage) return null;

  let raw: string | null;
  try {
    raw = storage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let entry: StorageEntry;
  try {
    entry = JSON.parse(raw) as StorageEntry;
  } catch {
    // Битый JSON — выкидываем, чтобы не оставлять зависшие данные.
    clearSession(storage);
    return null;
  }

  // Проверка версии — если схема в коде эволюционировала,
  // старые данные несовместимы → стираем.
  if (entry.version !== CURRENT_VERSION) {
    clearSession(storage);
    return null;
  }

  // TTL.
  if (Date.now() - entry.timestamp > TTL_MS) {
    clearSession(storage);
    return null;
  }

  return entry.data;
}

/**
 * Удаляет запись. Используется при истечении TTL, рассинхроне версии
 * или явном клике «Начать заново» в модалке M4 (опционально).
 */
export function clearSession(storage: StorageLike | null = getDefaultStorage()): void {
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Предикат: загруженная сессия — незавершённая (ребёнок известен,
 * есть хотя бы один ответ, тест не дошёл до 'done')?
 *
 * Если true → показываем модалку M4 (UX-спека 3.2).
 * Если false → или сессии нет, или она «использована» / в финализации.
 *
 * Phase 4 day 2: 3 early exits — М4 «Продолжить тест?» НЕ показываем когда:
 *  1. leadId !== null — backend подтвердил лид (M5 покажется первым).
 *  2. contacts !== null — форма ContactsForm заполнена, либо лид создан
 *     (M5 / outbox-flow), либо submit ещё в процессе (M4 неуместен).
 *  3. outbox !== null — outbox в работе, resume-effect делает auto-redirect
 *     на /result где retry разрулит. M4 не нужен.
 */
export function isUnfinishedSession(state: SessionState): boolean {
  if (state.leadId !== null) return false;
  if (state.contacts !== null) return false;
  if (state.outbox !== null) return false;
  return (
    state.child !== null &&
    (state.stage1Answers.length > 0 || state.stage2Answers.length > 0) &&
    state.stage !== 'done'
  );
}
